/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Api
 * AccelerateByte Agency API
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  ContactFormBody,
  CreatePostBody,
  CreateProjectBody,
  CreateServiceBody,
  CreateTeamMemberBody,
  CreateTestimonialBody,
  DashboardStats,
  HealthStatus,
  Lead,
  LeadsListResponse,
  ListLeadsParams,
  ListPostsParams,
  ListProjectsParams,
  Post,
  PostsListResponse,
  Project,
  PublicStats,
  Service,
  SuccessResponse,
  TeamMember,
  Testimonial,
  UpdateLeadStatusBody,
  UpdatePostBody,
  UpdateProjectBody,
  UpdateServiceBody,
  UpdateTeamMemberBody,
  UpdateTestimonialBody,
} from "./api.schemas";

import { customFetch } from "../custom-fetch";
import type { ErrorType, BodyType } from "../custom-fetch";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Returns server health status
 * @summary Health check
 */
export const getHealthCheckUrl = () => {
  return `/api/healthz`;
};

export const healthCheck = async (
  options?: RequestInit,
): Promise<HealthStatus> => {
  return customFetch<HealthStatus>(getHealthCheckUrl(), {
    ...options,
    method: "GET",
  });
};

export const getHealthCheckQueryKey = () => {
  return [`/api/healthz`] as const;
};

export const getHealthCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthCheckQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof healthCheck>>> = ({
    signal,
  }) => healthCheck({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type HealthCheckQueryResult = NonNullable<
  Awaited<ReturnType<typeof healthCheck>>
>;
export type HealthCheckQueryError = ErrorType<unknown>;

/**
 * @summary Health check
 */

export function useHealthCheck<
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getHealthCheckQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List published blog posts
 */
export const getListPostsUrl = (params?: ListPostsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/posts?${stringifiedParams}`
    : `/api/posts`;
};

export const listPosts = async (
  params?: ListPostsParams,
  options?: RequestInit,
): Promise<PostsListResponse> => {
  return customFetch<PostsListResponse>(getListPostsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListPostsQueryKey = (params?: ListPostsParams) => {
  return [`/api/posts`, ...(params ? [params] : [])] as const;
};

export const getListPostsQueryOptions = <
  TData = Awaited<ReturnType<typeof listPosts>>,
  TError = ErrorType<unknown>,
>(
  params?: ListPostsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listPosts>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListPostsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listPosts>>> = ({
    signal,
  }) => listPosts(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listPosts>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListPostsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listPosts>>
>;
export type ListPostsQueryError = ErrorType<unknown>;

/**
 * @summary List published blog posts
 */

export function useListPosts<
  TData = Awaited<ReturnType<typeof listPosts>>,
  TError = ErrorType<unknown>,
>(
  params?: ListPostsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listPosts>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListPostsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create blog post (admin)
 */
export const getCreatePostUrl = () => {
  return `/api/posts`;
};

export const createPost = async (
  createPostBody: CreatePostBody,
  options?: RequestInit,
): Promise<Post> => {
  return customFetch<Post>(getCreatePostUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createPostBody),
  });
};

export const getCreatePostMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPost>>,
    TError,
    { data: BodyType<CreatePostBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPost>>,
  TError,
  { data: BodyType<CreatePostBody> },
  TContext
> => {
  const mutationKey = ["createPost"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPost>>,
    { data: BodyType<CreatePostBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createPost(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePostMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPost>>
>;
export type CreatePostMutationBody = BodyType<CreatePostBody>;
export type CreatePostMutationError = ErrorType<unknown>;

/**
 * @summary Create blog post (admin)
 */
export const useCreatePost = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPost>>,
    TError,
    { data: BodyType<CreatePostBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createPost>>,
  TError,
  { data: BodyType<CreatePostBody> },
  TContext
> => {
  return useMutation(getCreatePostMutationOptions(options));
};

/**
 * @summary Get a single blog post by slug
 */
export const getGetPostUrl = (slug: string) => {
  return `/api/posts/${slug}`;
};

export const getPost = async (
  slug: string,
  options?: RequestInit,
): Promise<Post> => {
  return customFetch<Post>(getGetPostUrl(slug), {
    ...options,
    method: "GET",
  });
};

export const getGetPostQueryKey = (slug: string) => {
  return [`/api/posts/${slug}`] as const;
};

export const getGetPostQueryOptions = <
  TData = Awaited<ReturnType<typeof getPost>>,
  TError = ErrorType<void>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getPost>>, TError, TData>;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPostQueryKey(slug);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPost>>> = ({
    signal,
  }) => getPost(slug, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!slug,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getPost>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetPostQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPost>>
>;
export type GetPostQueryError = ErrorType<void>;

/**
 * @summary Get a single blog post by slug
 */

export function useGetPost<
  TData = Awaited<ReturnType<typeof getPost>>,
  TError = ErrorType<void>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getPost>>, TError, TData>;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetPostQueryOptions(slug, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update a blog post (admin)
 */
export const getUpdatePostUrl = (id: string) => {
  return `/api/posts/${id}/update`;
};

export const updatePost = async (
  id: string,
  updatePostBody: UpdatePostBody,
  options?: RequestInit,
): Promise<Post> => {
  return customFetch<Post>(getUpdatePostUrl(id), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updatePostBody),
  });
};

export const getUpdatePostMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updatePost>>,
    TError,
    { id: string; data: BodyType<UpdatePostBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updatePost>>,
  TError,
  { id: string; data: BodyType<UpdatePostBody> },
  TContext
> => {
  const mutationKey = ["updatePost"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updatePost>>,
    { id: string; data: BodyType<UpdatePostBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updatePost(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdatePostMutationResult = NonNullable<
  Awaited<ReturnType<typeof updatePost>>
>;
export type UpdatePostMutationBody = BodyType<UpdatePostBody>;
export type UpdatePostMutationError = ErrorType<unknown>;

/**
 * @summary Update a blog post (admin)
 */
export const useUpdatePost = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updatePost>>,
    TError,
    { id: string; data: BodyType<UpdatePostBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updatePost>>,
  TError,
  { id: string; data: BodyType<UpdatePostBody> },
  TContext
> => {
  return useMutation(getUpdatePostMutationOptions(options));
};

/**
 * @summary Delete a blog post (admin)
 */
export const getDeletePostUrl = (id: string) => {
  return `/api/posts/${id}/delete`;
};

export const deletePost = async (
  id: string,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeletePostUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeletePostMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePost>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deletePost>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["deletePost"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deletePost>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deletePost(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeletePostMutationResult = NonNullable<
  Awaited<ReturnType<typeof deletePost>>
>;

export type DeletePostMutationError = ErrorType<unknown>;

/**
 * @summary Delete a blog post (admin)
 */
export const useDeletePost = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePost>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deletePost>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getDeletePostMutationOptions(options));
};

/**
 * @summary List all projects
 */
export const getListProjectsUrl = (params?: ListProjectsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/projects?${stringifiedParams}`
    : `/api/projects`;
};

export const listProjects = async (
  params?: ListProjectsParams,
  options?: RequestInit,
): Promise<Project[]> => {
  return customFetch<Project[]>(getListProjectsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListProjectsQueryKey = (params?: ListProjectsParams) => {
  return [`/api/projects`, ...(params ? [params] : [])] as const;
};

export const getListProjectsQueryOptions = <
  TData = Awaited<ReturnType<typeof listProjects>>,
  TError = ErrorType<unknown>,
>(
  params?: ListProjectsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listProjects>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListProjectsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listProjects>>> = ({
    signal,
  }) => listProjects(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listProjects>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListProjectsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listProjects>>
>;
export type ListProjectsQueryError = ErrorType<unknown>;

/**
 * @summary List all projects
 */

export function useListProjects<
  TData = Awaited<ReturnType<typeof listProjects>>,
  TError = ErrorType<unknown>,
>(
  params?: ListProjectsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listProjects>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListProjectsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create a project (admin)
 */
export const getCreateProjectUrl = () => {
  return `/api/projects`;
};

export const createProject = async (
  createProjectBody: CreateProjectBody,
  options?: RequestInit,
): Promise<Project> => {
  return customFetch<Project>(getCreateProjectUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createProjectBody),
  });
};

export const getCreateProjectMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createProject>>,
    TError,
    { data: BodyType<CreateProjectBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createProject>>,
  TError,
  { data: BodyType<CreateProjectBody> },
  TContext
> => {
  const mutationKey = ["createProject"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createProject>>,
    { data: BodyType<CreateProjectBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createProject(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateProjectMutationResult = NonNullable<
  Awaited<ReturnType<typeof createProject>>
>;
export type CreateProjectMutationBody = BodyType<CreateProjectBody>;
export type CreateProjectMutationError = ErrorType<unknown>;

/**
 * @summary Create a project (admin)
 */
export const useCreateProject = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createProject>>,
    TError,
    { data: BodyType<CreateProjectBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createProject>>,
  TError,
  { data: BodyType<CreateProjectBody> },
  TContext
> => {
  return useMutation(getCreateProjectMutationOptions(options));
};

/**
 * @summary Get a single project by slug
 */
export const getGetProjectUrl = (slug: string) => {
  return `/api/projects/${slug}`;
};

export const getProject = async (
  slug: string,
  options?: RequestInit,
): Promise<Project> => {
  return customFetch<Project>(getGetProjectUrl(slug), {
    ...options,
    method: "GET",
  });
};

export const getGetProjectQueryKey = (slug: string) => {
  return [`/api/projects/${slug}`] as const;
};

export const getGetProjectQueryOptions = <
  TData = Awaited<ReturnType<typeof getProject>>,
  TError = ErrorType<void>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getProject>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProjectQueryKey(slug);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProject>>> = ({
    signal,
  }) => getProject(slug, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!slug,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getProject>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetProjectQueryResult = NonNullable<
  Awaited<ReturnType<typeof getProject>>
>;
export type GetProjectQueryError = ErrorType<void>;

/**
 * @summary Get a single project by slug
 */

export function useGetProject<
  TData = Awaited<ReturnType<typeof getProject>>,
  TError = ErrorType<void>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getProject>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetProjectQueryOptions(slug, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update a project (admin)
 */
export const getUpdateProjectUrl = (id: string) => {
  return `/api/projects/${id}/update`;
};

export const updateProject = async (
  id: string,
  updateProjectBody: UpdateProjectBody,
  options?: RequestInit,
): Promise<Project> => {
  return customFetch<Project>(getUpdateProjectUrl(id), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateProjectBody),
  });
};

export const getUpdateProjectMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateProject>>,
    TError,
    { id: string; data: BodyType<UpdateProjectBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateProject>>,
  TError,
  { id: string; data: BodyType<UpdateProjectBody> },
  TContext
> => {
  const mutationKey = ["updateProject"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateProject>>,
    { id: string; data: BodyType<UpdateProjectBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateProject(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateProjectMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateProject>>
>;
export type UpdateProjectMutationBody = BodyType<UpdateProjectBody>;
export type UpdateProjectMutationError = ErrorType<unknown>;

/**
 * @summary Update a project (admin)
 */
export const useUpdateProject = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateProject>>,
    TError,
    { id: string; data: BodyType<UpdateProjectBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateProject>>,
  TError,
  { id: string; data: BodyType<UpdateProjectBody> },
  TContext
> => {
  return useMutation(getUpdateProjectMutationOptions(options));
};

/**
 * @summary Delete a project (admin)
 */
export const getDeleteProjectUrl = (id: string) => {
  return `/api/projects/${id}/delete`;
};

export const deleteProject = async (
  id: string,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteProjectUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteProjectMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteProject>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteProject>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["deleteProject"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteProject>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteProject(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteProjectMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteProject>>
>;

export type DeleteProjectMutationError = ErrorType<unknown>;

/**
 * @summary Delete a project (admin)
 */
export const useDeleteProject = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteProject>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteProject>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getDeleteProjectMutationOptions(options));
};

/**
 * @summary List all services
 */
export const getListServicesUrl = () => {
  return `/api/services`;
};

export const listServices = async (
  options?: RequestInit,
): Promise<Service[]> => {
  return customFetch<Service[]>(getListServicesUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListServicesQueryKey = () => {
  return [`/api/services`] as const;
};

export const getListServicesQueryOptions = <
  TData = Awaited<ReturnType<typeof listServices>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listServices>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListServicesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listServices>>> = ({
    signal,
  }) => listServices({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listServices>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListServicesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listServices>>
>;
export type ListServicesQueryError = ErrorType<unknown>;

/**
 * @summary List all services
 */

export function useListServices<
  TData = Awaited<ReturnType<typeof listServices>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listServices>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListServicesQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create a service (admin)
 */
export const getCreateServiceUrl = () => {
  return `/api/services`;
};

export const createService = async (
  createServiceBody: CreateServiceBody,
  options?: RequestInit,
): Promise<Service> => {
  return customFetch<Service>(getCreateServiceUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createServiceBody),
  });
};

export const getCreateServiceMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createService>>,
    TError,
    { data: BodyType<CreateServiceBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createService>>,
  TError,
  { data: BodyType<CreateServiceBody> },
  TContext
> => {
  const mutationKey = ["createService"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createService>>,
    { data: BodyType<CreateServiceBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createService(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateServiceMutationResult = NonNullable<
  Awaited<ReturnType<typeof createService>>
>;
export type CreateServiceMutationBody = BodyType<CreateServiceBody>;
export type CreateServiceMutationError = ErrorType<unknown>;

/**
 * @summary Create a service (admin)
 */
export const useCreateService = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createService>>,
    TError,
    { data: BodyType<CreateServiceBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createService>>,
  TError,
  { data: BodyType<CreateServiceBody> },
  TContext
> => {
  return useMutation(getCreateServiceMutationOptions(options));
};

/**
 * @summary Get a single service by slug
 */
export const getGetServiceUrl = (slug: string) => {
  return `/api/services/${slug}`;
};

export const getService = async (
  slug: string,
  options?: RequestInit,
): Promise<Service> => {
  return customFetch<Service>(getGetServiceUrl(slug), {
    ...options,
    method: "GET",
  });
};

export const getGetServiceQueryKey = (slug: string) => {
  return [`/api/services/${slug}`] as const;
};

export const getGetServiceQueryOptions = <
  TData = Awaited<ReturnType<typeof getService>>,
  TError = ErrorType<void>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getService>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetServiceQueryKey(slug);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getService>>> = ({
    signal,
  }) => getService(slug, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!slug,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getService>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetServiceQueryResult = NonNullable<
  Awaited<ReturnType<typeof getService>>
>;
export type GetServiceQueryError = ErrorType<void>;

/**
 * @summary Get a single service by slug
 */

export function useGetService<
  TData = Awaited<ReturnType<typeof getService>>,
  TError = ErrorType<void>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getService>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetServiceQueryOptions(slug, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update a service (admin)
 */
export const getUpdateServiceUrl = (id: string) => {
  return `/api/services/${id}/update`;
};

export const updateService = async (
  id: string,
  updateServiceBody: UpdateServiceBody,
  options?: RequestInit,
): Promise<Service> => {
  return customFetch<Service>(getUpdateServiceUrl(id), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateServiceBody),
  });
};

export const getUpdateServiceMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateService>>,
    TError,
    { id: string; data: BodyType<UpdateServiceBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateService>>,
  TError,
  { id: string; data: BodyType<UpdateServiceBody> },
  TContext
> => {
  const mutationKey = ["updateService"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateService>>,
    { id: string; data: BodyType<UpdateServiceBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateService(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateServiceMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateService>>
>;
export type UpdateServiceMutationBody = BodyType<UpdateServiceBody>;
export type UpdateServiceMutationError = ErrorType<unknown>;

/**
 * @summary Update a service (admin)
 */
export const useUpdateService = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateService>>,
    TError,
    { id: string; data: BodyType<UpdateServiceBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateService>>,
  TError,
  { id: string; data: BodyType<UpdateServiceBody> },
  TContext
> => {
  return useMutation(getUpdateServiceMutationOptions(options));
};

/**
 * @summary Delete a service (admin)
 */
export const getDeleteServiceUrl = (id: string) => {
  return `/api/services/${id}/delete`;
};

export const deleteService = async (
  id: string,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteServiceUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteServiceMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteService>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteService>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["deleteService"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteService>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteService(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteServiceMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteService>>
>;

export type DeleteServiceMutationError = ErrorType<unknown>;

/**
 * @summary Delete a service (admin)
 */
export const useDeleteService = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteService>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteService>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getDeleteServiceMutationOptions(options));
};

/**
 * @summary List all leads (admin)
 */
export const getListLeadsUrl = (params?: ListLeadsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/leads?${stringifiedParams}`
    : `/api/leads`;
};

export const listLeads = async (
  params?: ListLeadsParams,
  options?: RequestInit,
): Promise<LeadsListResponse> => {
  return customFetch<LeadsListResponse>(getListLeadsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListLeadsQueryKey = (params?: ListLeadsParams) => {
  return [`/api/leads`, ...(params ? [params] : [])] as const;
};

export const getListLeadsQueryOptions = <
  TData = Awaited<ReturnType<typeof listLeads>>,
  TError = ErrorType<unknown>,
>(
  params?: ListLeadsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listLeads>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListLeadsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listLeads>>> = ({
    signal,
  }) => listLeads(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listLeads>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListLeadsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listLeads>>
>;
export type ListLeadsQueryError = ErrorType<unknown>;

/**
 * @summary List all leads (admin)
 */

export function useListLeads<
  TData = Awaited<ReturnType<typeof listLeads>>,
  TError = ErrorType<unknown>,
>(
  params?: ListLeadsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listLeads>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListLeadsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Submit contact form (creates a lead)
 */
export const getSubmitContactUrl = () => {
  return `/api/contact`;
};

export const submitContact = async (
  contactFormBody: ContactFormBody,
  options?: RequestInit,
): Promise<SuccessResponse> => {
  return customFetch<SuccessResponse>(getSubmitContactUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(contactFormBody),
  });
};

export const getSubmitContactMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof submitContact>>,
    TError,
    { data: BodyType<ContactFormBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof submitContact>>,
  TError,
  { data: BodyType<ContactFormBody> },
  TContext
> => {
  const mutationKey = ["submitContact"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof submitContact>>,
    { data: BodyType<ContactFormBody> }
  > = (props) => {
    const { data } = props ?? {};

    return submitContact(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SubmitContactMutationResult = NonNullable<
  Awaited<ReturnType<typeof submitContact>>
>;
export type SubmitContactMutationBody = BodyType<ContactFormBody>;
export type SubmitContactMutationError = ErrorType<void>;

/**
 * @summary Submit contact form (creates a lead)
 */
export const useSubmitContact = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof submitContact>>,
    TError,
    { data: BodyType<ContactFormBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof submitContact>>,
  TError,
  { data: BodyType<ContactFormBody> },
  TContext
> => {
  return useMutation(getSubmitContactMutationOptions(options));
};

/**
 * @summary Update lead status (admin)
 */
export const getUpdateLeadStatusUrl = (id: string) => {
  return `/api/leads/${id}/status`;
};

export const updateLeadStatus = async (
  id: string,
  updateLeadStatusBody: UpdateLeadStatusBody,
  options?: RequestInit,
): Promise<Lead> => {
  return customFetch<Lead>(getUpdateLeadStatusUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateLeadStatusBody),
  });
};

export const getUpdateLeadStatusMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateLeadStatus>>,
    TError,
    { id: string; data: BodyType<UpdateLeadStatusBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateLeadStatus>>,
  TError,
  { id: string; data: BodyType<UpdateLeadStatusBody> },
  TContext
> => {
  const mutationKey = ["updateLeadStatus"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateLeadStatus>>,
    { id: string; data: BodyType<UpdateLeadStatusBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateLeadStatus(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateLeadStatusMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateLeadStatus>>
>;
export type UpdateLeadStatusMutationBody = BodyType<UpdateLeadStatusBody>;
export type UpdateLeadStatusMutationError = ErrorType<unknown>;

/**
 * @summary Update lead status (admin)
 */
export const useUpdateLeadStatus = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateLeadStatus>>,
    TError,
    { id: string; data: BodyType<UpdateLeadStatusBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateLeadStatus>>,
  TError,
  { id: string; data: BodyType<UpdateLeadStatusBody> },
  TContext
> => {
  return useMutation(getUpdateLeadStatusMutationOptions(options));
};

/**
 * @summary Delete a lead (admin)
 */
export const getDeleteLeadUrl = (id: string) => {
  return `/api/leads/${id}/delete`;
};

export const deleteLead = async (
  id: string,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteLeadUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteLeadMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteLead>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteLead>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["deleteLead"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteLead>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteLead(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteLeadMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteLead>>
>;

export type DeleteLeadMutationError = ErrorType<unknown>;

/**
 * @summary Delete a lead (admin)
 */
export const useDeleteLead = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteLead>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteLead>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getDeleteLeadMutationOptions(options));
};

/**
 * @summary List published testimonials
 */
export const getListTestimonialsUrl = () => {
  return `/api/testimonials`;
};

export const listTestimonials = async (
  options?: RequestInit,
): Promise<Testimonial[]> => {
  return customFetch<Testimonial[]>(getListTestimonialsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListTestimonialsQueryKey = () => {
  return [`/api/testimonials`] as const;
};

export const getListTestimonialsQueryOptions = <
  TData = Awaited<ReturnType<typeof listTestimonials>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listTestimonials>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTestimonialsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listTestimonials>>
  > = ({ signal }) => listTestimonials({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listTestimonials>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListTestimonialsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listTestimonials>>
>;
export type ListTestimonialsQueryError = ErrorType<unknown>;

/**
 * @summary List published testimonials
 */

export function useListTestimonials<
  TData = Awaited<ReturnType<typeof listTestimonials>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listTestimonials>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListTestimonialsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create a testimonial (admin)
 */
export const getCreateTestimonialUrl = () => {
  return `/api/testimonials`;
};

export const createTestimonial = async (
  createTestimonialBody: CreateTestimonialBody,
  options?: RequestInit,
): Promise<Testimonial> => {
  return customFetch<Testimonial>(getCreateTestimonialUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createTestimonialBody),
  });
};

export const getCreateTestimonialMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTestimonial>>,
    TError,
    { data: BodyType<CreateTestimonialBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createTestimonial>>,
  TError,
  { data: BodyType<CreateTestimonialBody> },
  TContext
> => {
  const mutationKey = ["createTestimonial"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createTestimonial>>,
    { data: BodyType<CreateTestimonialBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createTestimonial(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTestimonialMutationResult = NonNullable<
  Awaited<ReturnType<typeof createTestimonial>>
>;
export type CreateTestimonialMutationBody = BodyType<CreateTestimonialBody>;
export type CreateTestimonialMutationError = ErrorType<unknown>;

/**
 * @summary Create a testimonial (admin)
 */
export const useCreateTestimonial = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTestimonial>>,
    TError,
    { data: BodyType<CreateTestimonialBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createTestimonial>>,
  TError,
  { data: BodyType<CreateTestimonialBody> },
  TContext
> => {
  return useMutation(getCreateTestimonialMutationOptions(options));
};

/**
 * @summary Update a testimonial (admin)
 */
export const getUpdateTestimonialUrl = (id: string) => {
  return `/api/testimonials/${id}/update`;
};

export const updateTestimonial = async (
  id: string,
  updateTestimonialBody: UpdateTestimonialBody,
  options?: RequestInit,
): Promise<Testimonial> => {
  return customFetch<Testimonial>(getUpdateTestimonialUrl(id), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateTestimonialBody),
  });
};

export const getUpdateTestimonialMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTestimonial>>,
    TError,
    { id: string; data: BodyType<UpdateTestimonialBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateTestimonial>>,
  TError,
  { id: string; data: BodyType<UpdateTestimonialBody> },
  TContext
> => {
  const mutationKey = ["updateTestimonial"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateTestimonial>>,
    { id: string; data: BodyType<UpdateTestimonialBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateTestimonial(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTestimonialMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateTestimonial>>
>;
export type UpdateTestimonialMutationBody = BodyType<UpdateTestimonialBody>;
export type UpdateTestimonialMutationError = ErrorType<unknown>;

/**
 * @summary Update a testimonial (admin)
 */
export const useUpdateTestimonial = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTestimonial>>,
    TError,
    { id: string; data: BodyType<UpdateTestimonialBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateTestimonial>>,
  TError,
  { id: string; data: BodyType<UpdateTestimonialBody> },
  TContext
> => {
  return useMutation(getUpdateTestimonialMutationOptions(options));
};

/**
 * @summary List published team members
 */
export const getListTeamMembersUrl = () => {
  return `/api/team`;
};

export const listTeamMembers = async (
  options?: RequestInit,
): Promise<TeamMember[]> => {
  return customFetch<TeamMember[]>(getListTeamMembersUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListTeamMembersQueryKey = () => {
  return [`/api/team`] as const;
};

export const getListTeamMembersQueryOptions = <
  TData = Awaited<ReturnType<typeof listTeamMembers>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listTeamMembers>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTeamMembersQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listTeamMembers>>> = ({
    signal,
  }) => listTeamMembers({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listTeamMembers>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListTeamMembersQueryResult = NonNullable<
  Awaited<ReturnType<typeof listTeamMembers>>
>;
export type ListTeamMembersQueryError = ErrorType<unknown>;

/**
 * @summary List published team members
 */

export function useListTeamMembers<
  TData = Awaited<ReturnType<typeof listTeamMembers>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listTeamMembers>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListTeamMembersQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create a team member (admin)
 */
export const getCreateTeamMemberUrl = () => {
  return `/api/team`;
};

export const createTeamMember = async (
  createTeamMemberBody: CreateTeamMemberBody,
  options?: RequestInit,
): Promise<TeamMember> => {
  return customFetch<TeamMember>(getCreateTeamMemberUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createTeamMemberBody),
  });
};

export const getCreateTeamMemberMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTeamMember>>,
    TError,
    { data: BodyType<CreateTeamMemberBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createTeamMember>>,
  TError,
  { data: BodyType<CreateTeamMemberBody> },
  TContext
> => {
  const mutationKey = ["createTeamMember"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createTeamMember>>,
    { data: BodyType<CreateTeamMemberBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createTeamMember(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTeamMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof createTeamMember>>
>;
export type CreateTeamMemberMutationBody = BodyType<CreateTeamMemberBody>;
export type CreateTeamMemberMutationError = ErrorType<unknown>;

/**
 * @summary Create a team member (admin)
 */
export const useCreateTeamMember = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTeamMember>>,
    TError,
    { data: BodyType<CreateTeamMemberBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createTeamMember>>,
  TError,
  { data: BodyType<CreateTeamMemberBody> },
  TContext
> => {
  return useMutation(getCreateTeamMemberMutationOptions(options));
};

/**
 * @summary Update a team member (admin)
 */
export const getUpdateTeamMemberUrl = (id: string) => {
  return `/api/team/${id}/update`;
};

export const updateTeamMember = async (
  id: string,
  updateTeamMemberBody: UpdateTeamMemberBody,
  options?: RequestInit,
): Promise<TeamMember> => {
  return customFetch<TeamMember>(getUpdateTeamMemberUrl(id), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateTeamMemberBody),
  });
};

export const getUpdateTeamMemberMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTeamMember>>,
    TError,
    { id: string; data: BodyType<UpdateTeamMemberBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateTeamMember>>,
  TError,
  { id: string; data: BodyType<UpdateTeamMemberBody> },
  TContext
> => {
  const mutationKey = ["updateTeamMember"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateTeamMember>>,
    { id: string; data: BodyType<UpdateTeamMemberBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateTeamMember(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTeamMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateTeamMember>>
>;
export type UpdateTeamMemberMutationBody = BodyType<UpdateTeamMemberBody>;
export type UpdateTeamMemberMutationError = ErrorType<unknown>;

/**
 * @summary Update a team member (admin)
 */
export const useUpdateTeamMember = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTeamMember>>,
    TError,
    { id: string; data: BodyType<UpdateTeamMemberBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateTeamMember>>,
  TError,
  { id: string; data: BodyType<UpdateTeamMemberBody> },
  TContext
> => {
  return useMutation(getUpdateTeamMemberMutationOptions(options));
};

/**
 * @summary Get dashboard stats (admin)
 */
export const getGetDashboardStatsUrl = () => {
  return `/api/stats`;
};

export const getDashboardStats = async (
  options?: RequestInit,
): Promise<DashboardStats> => {
  return customFetch<DashboardStats>(getGetDashboardStatsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetDashboardStatsQueryKey = () => {
  return [`/api/stats`] as const;
};

export const getGetDashboardStatsQueryOptions = <
  TData = Awaited<ReturnType<typeof getDashboardStats>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getDashboardStats>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDashboardStatsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getDashboardStats>>
  > = ({ signal }) => getDashboardStats({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getDashboardStats>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetDashboardStatsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDashboardStats>>
>;
export type GetDashboardStatsQueryError = ErrorType<unknown>;

/**
 * @summary Get dashboard stats (admin)
 */

export function useGetDashboardStats<
  TData = Awaited<ReturnType<typeof getDashboardStats>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getDashboardStats>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetDashboardStatsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get public-facing stats (projects, clients, etc.)
 */
export const getGetPublicStatsUrl = () => {
  return `/api/stats/public`;
};

export const getPublicStats = async (
  options?: RequestInit,
): Promise<PublicStats> => {
  return customFetch<PublicStats>(getGetPublicStatsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetPublicStatsQueryKey = () => {
  return [`/api/stats/public`] as const;
};

export const getGetPublicStatsQueryOptions = <
  TData = Awaited<ReturnType<typeof getPublicStats>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getPublicStats>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPublicStatsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPublicStats>>> = ({
    signal,
  }) => getPublicStats({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPublicStats>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetPublicStatsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPublicStats>>
>;
export type GetPublicStatsQueryError = ErrorType<unknown>;

/**
 * @summary Get public-facing stats (projects, clients, etc.)
 */

export function useGetPublicStats<
  TData = Awaited<ReturnType<typeof getPublicStats>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getPublicStats>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetPublicStatsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}
